// components/version-update-dialog.tsx — the blocking modal shown when
// useAppVersion detects the loaded bundle is out of date. Wraps Radix
// AlertDialog (forbidden list in AGENTS.md §1.7 forbids native
// window.confirm/alert/prompt — and the dialog also has to be modal +
// focus-trapped, which Radix gives us for free).
//
// Kept as a dumb controlled component so the trigger that owns the
// "block on next user action" capture listener (version-update-trigger.tsx)
// can flip `open` whenever the user clicks / types.

import { RotateCw } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type VersionUpdateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReload: () => void;
  clientVersion: string;
  serverVersion: string;
};

export function VersionUpdateDialog({
  open,
  onOpenChange,
  onReload,
  clientVersion,
  serverVersion,
}: VersionUpdateDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="version-update-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>New version available</AlertDialogTitle>
          <AlertDialogDescription>
            A new version of CVMLake is ready. Reload to apply the update — you won&apos;t lose any
            unsaved work on this page.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs">
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Running</span>
            <span data-testid="version-update-client">{clientVersion}</span>
          </div>
          <div className="mt-1 flex justify-between gap-3">
            <span className="text-muted-foreground">Available</span>
            <span data-testid="version-update-server">{serverVersion}</span>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="version-update-later">Later</AlertDialogCancel>
          <AlertDialogAction
            data-testid="version-update-reload"
            onClick={(e) => {
              e.preventDefault();
              onReload();
            }}
          >
            <RotateCw className="me-2 h-4 w-4" aria-hidden="true" />
            Reload now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
